/**
 * Feature flags — runtime-readable Vite env booleans.
 *
 * Convention: flag names match `VITE_<FLAG_NAME>_ENABLED`. Truthy is the
 * literal string `"true"` (case-sensitive). Anything else, including
 * `"1"`, `"yes"`, `"True"`, unset, or empty, evaluates to false unless
 * the per-flag default is true. Strict matching prevents silent flips
 * from typos.
 *
 * Some flags default true (an existing capability we're keeping on);
 * others default false (a capability we're rolling out gradually). Each
 * flag below documents its own default.
 */

function readFlag(
  name: `VITE_${string}_ENABLED`,
  defaultValue: boolean,
): boolean {
  const env = (import.meta as ImportMeta).env;
  const raw = (env as Record<string, string | undefined>)[name];
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  // Unset / empty / anything else falls through to the default.
  return defaultValue;
}

/**
 * v0.8.0 — magic-link auth.
 *
 * **Defaults FALSE.** Magic-link is the future state but ships dormant
 * until the cutover event coordinated with backend/ops/CRM/SMS.
 *
 * **Update v0.8.6: not mutually exclusive with PIN+DOB anymore.** Both
 * `MAGIC_LINK_ENABLED` and `PIN_LOGIN_ENABLED` can be true simultaneously
 * (the dual-mode-restoration model). The routing logic in AppRouter
 * handles all four combinations:
 *
 *   PIN=true  / ML=false  → bare URL → LoginPage (PIN+DOB only)
 *                            no `:token` route
 *   PIN=true  / ML=true   → bare URL → LoginPage + "Have a login code?" link
 *                            `:token` route active (magic-link landing)
 *                            `/login/code` → manual magic-link entry
 *   PIN=false / ML=true   → bare URL → ManualLoginPage (manual magic-link entry)
 *                            `:token` route active (magic-link landing)
 *   PIN=false / ML=false  → bare URL → LoginPage (PIN+DOB)
 *                            no `:token` route
 *                            (safety fallback — at least one entry must work)
 *
 * Per decision ledger #44.
 */
export const MAGIC_LINK_ENABLED = readFlag('VITE_MAGIC_LINK_ENABLED', false);

/**
 * v0.8.6 — PIN+DOB auth restored (dual-mode with magic-link).
 *
 * **Defaults TRUE.** Reverses ledger #43 — PIN+DOB is restored to
 * supported status for the months-long window where statement-PIN
 * patients exist alongside magic-link patients. Each environment flips
 * the flag off when its issuing flows have fully migrated to
 * magic-link.
 *
 * The dual-mode coexistence is per backend's v0.8.5 kickoff: both
 * auth methods produce identical JWTs; PIN+DOB always lands at
 * patient-scope `/pay`; magic-link can be either patient-scope (`/pay`)
 * or appointment-scope (`/checkin/:id`).
 *
 * Routing implications documented inline above on MAGIC_LINK_ENABLED.
 *
 * Per decision ledger #53.
 */
export const PIN_LOGIN_ENABLED = readFlag('VITE_PIN_LOGIN_ENABLED', true);
